import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { StorageService } from 'src/app/common/services/storage/storage.service';
import { FileDTO } from '../../dtos/file.dto';
import {
  ChatFeedMessage,
  ChatMessage,
  ConversationResponse,
  ConversationsResponse,
  GenerateOptionsResponse,
  GlaiContentAPIResponse,
  GlaiPayload,
  GlaiUserFeedbackPayload,
  ProformSmartResponsePayload,
  ProformSmartResponseResult,
  TokenData,
} from '../../dtos/glai.dto';

@Injectable({
  providedIn: 'root',
})
export class GlaiService implements OnDestroy {
  private messageSubject = new Subject<ChatMessage>();

  public messages$ = this.messageSubject.asObservable();

  private streamAbortController: AbortController | null = null;

  private currentMessages = new Map<string, ChatMessage>();

  error: string | null = null;

  constructor(private apiService: APICoreService, private zone: NgZone) {}

  addUserMessage(content: string, uploadedFiles: FileDTO[]): void {
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content,
      isComplete: true,
      timestamp: new Date().toISOString(),
      role: 'user',
      files: uploadedFiles,
    };
    this.messageSubject.next(userMessage);
  }

  getConversationHistory(
    search = '',
    page = 1,
    per_page = 5
  ): Observable<ConversationsResponse> {
    return this.apiService.getRequest('ai/conversations', {
      ...(search ? { search } : {}),
      page,
      per_page,
    });
  }

  getConversation(conversationId: number): Observable<ConversationResponse> {
    return this.apiService.getRequest(`ai/conversation/${conversationId}`);
  }

  startConversation(
    message: string,
    messageFiles?: FileDTO[],
    modes?: string[]
  ): Observable<ConversationResponse> {
    return this.apiService.postJSONRequest('ai/conversation', {
      message,
      files: messageFiles,
      modes,
    });
  }

  continueConversation(
    conversationId: number,
    message: string,
    messageFiles?: FileDTO[],
    modes?: string[]
  ): Observable<ConversationResponse> {
    return this.apiService.postJSONRequest(
      `ai/conversation/${conversationId}/message`,
      { message, files: messageFiles, modes }
    );
  }

  async sendToStream(streamId: string, payload: GlaiPayload): Promise<void> {
    this.disconnectStream();

    const abortController = new AbortController();
    this.streamAbortController = abortController;

    const glaiApiUrl = EnvironmentService.glaiApiUrl();
    const token = StorageService.getAuthToken()?.token;

    try {
      const response = await fetch(
        `${glaiApiUrl}/chatbot/message/${streamId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
          signal: abortController.signal,
        }
      );

      if (!response.ok || !response.body) {
        throw new Error(`GLAI request failed with status ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        // eslint-disable-next-line no-await-in-loop -- sequential stream reads, cannot be parallelized
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder
          .decode(value, { stream: true })
          .replace(/\r\n/g, '\n');
        const rawEvents = buffer.split('\n\n');
        buffer = rawEvents.pop() ?? '';
        // reader.read() resolves outside NgZone (zone.js doesn't patch the
        // Streams API), so without this, message updates never trigger change
        // detection and the UI stops updating mid-stream even though the data
        // keeps arriving correctly.
        this.zone.run(() => {
          rawEvents.forEach((rawEvent) => this.handleSseEvent(rawEvent));
        });
      }
    } catch (error) {
      if ((error as { name?: string }).name !== 'AbortError') {
        this.zone.run(() => {
          this.error = `${error}`;
        });
      }
    }
  }

  private handleSseEvent(rawEvent: string): void {
    let eventType = 'message';
    const dataLines: string[] = [];

    rawEvent.split('\n').forEach((line) => {
      if (line.startsWith('event:')) {
        eventType = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trim());
      }
    });

    if (!dataLines.length) return;

    try {
      const tokenData: TokenData = JSON.parse(dataLines.join('\n'));
      if (eventType === 'token' || eventType === 'error') {
        this.handleTokenMessage(tokenData);
      }
      if (eventType === 'done' || eventType === 'complete') {
        this.markIncompleteMessagesComplete();
      }
    } catch (error) {
      this.error = `${error}`;
    }
  }

  private handleTokenMessage(tokenData: TokenData): void {
    const messageId = tokenData.message_id;

    if (this.currentMessages.has(messageId)) {
      const existingMessage = this.currentMessages.get(messageId);
      if (existingMessage) {
        existingMessage.rawContent =
          (existingMessage.rawContent || '') + tokenData.content;
        GlaiService.extractThinking(existingMessage);
        this.messageSubject.next({ ...existingMessage });
      }
    } else {
      const newMessage: ChatMessage = {
        id: messageId,
        rawContent: tokenData.content,
        content: tokenData.content,
        isComplete: false,
        timestamp: tokenData.timestamp,
        role: 'assistant',
      };
      GlaiService.extractThinking(newMessage);
      this.currentMessages.set(messageId, newMessage);
      this.messageSubject.next(newMessage);
    }
  }

  private markIncompleteMessagesComplete(): void {
    this.currentMessages.forEach((message) => {
      if (!message.isComplete) {
        message.isComplete = true;
        this.messageSubject.next({ ...message });
      }
    });
  }

  static extractThinking(message: ChatMessage | ChatFeedMessage): void {
    const source = message.rawContent || message.content;
    const thinkingParts = Array.from(
      source.matchAll(/<thinking>(.*?)<\/thinking>/gs)
    ).map((match) => match[1].trim());
    if (thinkingParts.length > 0) {
      message.thinking = thinkingParts.join('\n\n');
    }
    message.content = source.replace(/<thinking>.*?<\/thinking>/gs, '').trim();

    if (source.includes('<thinking>') && !source.includes('</thinking>')) {
      const parts = source.split('<thinking>');
      message.content = parts[0].trim();
      message.thinking = parts[1].trim();
    }
  }

  markMessageComplete(messageId: string): void {
    if (this.currentMessages.has(messageId)) {
      const message = this.currentMessages.get(messageId);
      if (message) {
        message.isComplete = true;
        this.messageSubject.next({ ...message });
      }
    }
  }

  disconnectStream(): void {
    if (this.streamAbortController) {
      this.streamAbortController.abort();
      this.streamAbortController = null;
    }
  }

  generateEgroweOptions(
    competencyId: number,
    strandId: number,
    level: number
  ): Observable<GenerateOptionsResponse> {
    return this.apiService.getRequest('ai/generate-content/egrowe-options', {
      competencyId,
      strandId,
      level,
    });
  }

  generateProformSmartResponse(
    payload: ProformSmartResponsePayload
  ): Observable<ProformSmartResponseResult> {
    return this.apiService.postJSONRequest(
      'ai/generate-content/proform-smart-response',
      payload
    );
  }

  findObservationSummary(
    coachlogId: number
  ): Observable<GlaiContentAPIResponse> {
    return this.apiService.getRequest(
      'ai/glai-content/find-observation-summary',
      { coachlog_id: coachlogId }
    );
  }

  summarizeObservationHistory(
    coachlogId: number,
    participantUserId: number,
    contextTableIds: number[]
  ): Observable<GlaiContentAPIResponse> {
    return this.apiService.getRequest(
      'ai/generate-content/summarize-observation-history',
      {
        coachlog_id: coachlogId,
        participant_user_id: participantUserId,
        context_table_ids: contextTableIds,
      }
    );
  }

  submitUserFeedback(
    conversationId: number,
    payload: GlaiUserFeedbackPayload
  ): Observable<unknown> {
    return this.apiService.postJSONRequest(
      `ai/conversation/${conversationId}/feedback`,
      payload
    );
  }

  submitGlaiContentFeedback(
    glaiContentId: number,
    userFeedback: string
  ): Observable<unknown> {
    return this.apiService.putJSONRequest(`ai/glai-content/${glaiContentId}`, {
      user_feedback: userFeedback,
    });
  }

  ngOnDestroy(): void {
    this.disconnectStream();
  }
}
